import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { TrendingUp, Clock, BarChart3, Info } from 'lucide-react';
import type {
  ResponseTimeTrendPoint,
  TicketVolumeTrendPoint,
  ResolutionRateTrendPoint,
} from '../../types/tickets';

interface InsightsChartsProps {
  responseTrendData: ResponseTimeTrendPoint[];
  ticketVolumeTrend: TicketVolumeTrendPoint[];
  resolutionRateTrend: ResolutionRateTrendPoint[];
  isLoading?: boolean;
}

// Chart info descriptions
const chartDescriptions = {
  responseTime: "Shows the average time for support to first respond to your tickets over the past 6 months. Lower times indicate faster initial responses.",
  ticketVolume: "Shows tickets you've created vs. resolved each month over the past 6 months. Helps track your support activity patterns.",
  resolutionRate: "Shows the percentage of your tickets resolved each month. Higher percentages indicate more effective issue resolution.",
};

// Info button component - works on both hover (desktop) and tap (mobile)
function InfoButton({ description }: { description: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [clickedOpen, setClickedOpen] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (clickedOpen) {
      // Close on second click
      setIsOpen(false);
      setClickedOpen(false);
    } else {
      // Open on first click
      setIsOpen(true);
      setClickedOpen(true);
    }
  };

  const handleOpenChange = (open: boolean) => {
    // Only allow hover to open, not close (close is handled by click or outside click)
    if (open && !clickedOpen) {
      setIsOpen(true);
    } else if (!open && !clickedOpen) {
      setIsOpen(false);
    }
  };

  const handleOutsideClick = () => {
    setIsOpen(false);
    setClickedOpen(false);
  };

  return (
    <TooltipProvider>
      <Tooltip
        delayDuration={300}
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <TooltipTrigger asChild>
          <button
            type="button"
            className="ml-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="More information"
            onClick={handleClick}
          >
            <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs text-sm"
          onPointerDownOutside={handleOutsideClick}
        >
          <p>{description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function InsightsCharts({
  responseTrendData,
  ticketVolumeTrend,
  resolutionRateTrend,
  isLoading = false,
}: InsightsChartsProps) {
  // Transform data for charts
  const responseChartData = responseTrendData.map((point) => ({
    name: point.label,
    value: point.averageResponseTimeHours,
  }));

  const volumeChartData = ticketVolumeTrend.map((point) => ({
    name: point.label,
    created: point.ticketsCreated,
    resolved: point.ticketsResolved,
  }));

  const resolutionChartData = resolutionRateTrend.map((point) => ({
    name: point.label,
    rate: point.resolutionRatePercent,
  }));

  interface TooltipPayloadEntry {
    name: string;
    value: number;
    color: string;
    unit?: string;
  }

  interface CustomTooltipProps {
    active?: boolean;
    payload?: TooltipPayloadEntry[];
    label?: string;
  }

  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <p className="font-semibold text-gray-900 dark:text-gray-50 mb-1">
            {label}
          </p>
          {payload.map((entry: TooltipPayloadEntry, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}{entry.unit || ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Check if all data is empty (no tickets)
  const hasNoData =
    responseChartData.every(d => d.value === 0) &&
    volumeChartData.every(d => d.created === 0 && d.resolved === 0) &&
    resolutionChartData.every(d => d.rate === 0);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className={i === 3 ? 'lg:col-span-2' : ''}>
            <CardHeader className="pb-4">
              <div className="h-5 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </CardHeader>
            <CardContent>
              <div className="h-[200px] bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (hasNoData) {
    return (
      <div className="text-center py-8">
        <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          No insights data yet
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          Once you have ticket activity, your insights will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Response Time Trend */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold flex items-center">
            <Clock className="h-4 w-4 mr-2" />
            Response Time Trend
            <InfoButton description={chartDescriptions.responseTime} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200} minHeight={200}>
            <AreaChart data={responseChartData}>
              <defs>
                <linearGradient id="responseGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
              />
              <YAxis
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
                label={{ value: 'Hours', angle: -90, position: 'insideLeft', fontSize: 12 }}
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#3b82f6"
                strokeWidth={2}
                fill="url(#responseGradient)"
                animationDuration={1000}
                name="Avg Response Time"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Monthly Ticket Volume */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold flex items-center">
            <BarChart3 className="h-4 w-4 mr-2" />
            Monthly Ticket Volume
            <InfoButton description={chartDescriptions.ticketVolume} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200} minHeight={200}>
            <BarChart data={volumeChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
              />
              <YAxis
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Bar
                dataKey="created"
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
                animationDuration={1000}
                name="Created"
              />
              <Bar
                dataKey="resolved"
                fill="#22c55e"
                radius={[4, 4, 0, 0]}
                animationDuration={1000}
                name="Resolved"
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Resolution Rate */}
      <Card className="lg:col-span-2">
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold flex items-center">
            <TrendingUp className="h-4 w-4 mr-2" />
            Resolution Rate Over Time
            <InfoButton description={chartDescriptions.resolutionRate} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200} minHeight={200}>
            <LineChart data={resolutionChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
              />
              <YAxis
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
                domain={[0, 100]}
                label={{ value: 'Rate (%)', angle: -90, position: 'insideLeft', fontSize: 12 }}
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="rate"
                stroke="#22c55e"
                strokeWidth={3}
                dot={{ fill: '#22c55e', r: 4 }}
                activeDot={{ r: 6 }}
                animationDuration={1000}
                name="Resolution Rate (%)"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
